import React, { useEffect, useMemo, useState } from 'react';
import { TodoItem } from '../lib/todos';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { getPreferredTimeZone, getGmtOffsetLabel, getHourInTimeZone, formatHourLabel } from '../utils/timezone';

interface WeekViewProps {
    currentWeek: Date;
    items: TodoItem[];
    selectedDay?: Date; // Add selectedDay prop
    onWeekChange: (newWeek: Date) => void;
    onEventClick: (id: string) => void;
    onTimeSlotClick: (date: Date, hour: number) => void;
}

function startOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
}

function addWeeks(date: Date, weeks: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + (weeks * 7));
    return result;
}

function addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

function formatTime(hour: number): string {
    // Deprecated in favor of formatHourLabel with tz, but keep as fallback
    if (hour === 0) return '12 AM';
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return '12 PM';
    return `${hour - 12} PM`;
}

function ymdLocal(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function isToday(date: Date): boolean {
    const today = new Date();
    return ymdLocal(date) === ymdLocal(today);
}

function getEventPosition(event: TodoItem, date: Date): { top: number; height: number; time: string } {
    if (!event.due_date) {
        return { top: 0, height: 60, time: 'All day' };
    }

    const eventDate = new Date(event.due_date);
    const isAllDay = event.due_date.endsWith('T00:00:00.000Z');

    if (isAllDay) {
        return { top: 0, height: 60, time: 'All day' };
    }

    const hour = eventDate.getHours();
    const minute = eventDate.getMinutes();
    const top = (hour * 60) + minute; // 60px per hour
    const height = 60; // Default 1 hour duration
    const time = eventDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    return { top, height, time };
}

export default function WeekView({ currentWeek, items, selectedDay, onWeekChange, onEventClick, onTimeSlotClick }: WeekViewProps) {
    const weekStart = useMemo(() => startOfWeek(currentWeek), [currentWeek]);
    const weekDays = useMemo(() => {
        return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    }, [weekStart]);

    const hours = Array.from({ length: 24 }, (_, i) => i);

    // Preferred timezone
    const [timeZone, setTimeZone] = useState<string>(() => getPreferredTimeZone());
    useEffect(() => {
        const onTz = (e: any) => setTimeZone(e?.detail?.timeZone || getPreferredTimeZone());
        window.addEventListener('gv:timezoneChanged' as any, onTz);
        return () => window.removeEventListener('gv:timezoneChanged' as any, onTz);
    }, []);

    // Current time indicator (snap to hour; update hourly)
    const [now, setNow] = useState<Date>(new Date());
    useEffect(() => {
        const tick = () => setNow(new Date());
        const msToNextHour = (() => {
            const d = new Date();
            return (60 - d.getMinutes()) * 60000 - d.getSeconds() * 1000 - d.getMilliseconds();
        })();
        const timeout = setTimeout(() => {
            tick();
            const hourly = setInterval(tick, 60 * 60 * 1000);
            (window as any).__weekViewHourly = hourly;
        }, Math.max(0, msToNextHour));
        return () => {
            clearTimeout(timeout);
            const hourly = (window as any).__weekViewHourly; if (hourly) clearInterval(hourly);
        };
    }, []);
    const nowYmd = useMemo(() => ymdLocal(now), [now]);

    // Group events by day
    const eventsByDay = useMemo(() => {
        const grouped: { [key: string]: TodoItem[] } = {};

        weekDays.forEach(day => {
            const dayKey = ymdLocal(day);
            grouped[dayKey] = [];
        });

        items.forEach(item => {
            if (!item.due_date) return;
            const eventDate = new Date(item.due_date);
            const dayKey = ymdLocal(eventDate);
            if (grouped[dayKey]) {
                grouped[dayKey].push(item);
            }
        });

        return grouped;
    }, [items, weekDays]);

    const weekLabel = useMemo(() => {
        const start = weekDays[0];
        const end = weekDays[6];
        if (start.getMonth() === end.getMonth()) {
            return `${start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
        }
        return `${start.toLocaleDateString('en-US', { month: 'short' })} - ${end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
    }, [weekDays]);

    const tzLabel = useMemo(() => getGmtOffsetLabel(timeZone), [timeZone]);

    return (
        <div className="w-full bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">

            {/* Week Grid */}
            <div className="flex">
                {/* Time Column */}
                <div className="w-16 flex-shrink-0 border-r border-gray-800">
                    <div className="h-12 border-b border-gray-800 flex items-center justify-center text-[10px] text-gray-400">
                        {tzLabel}
                    </div>
                    {hours.map(hour => (
                        <div
                            key={hour}
                            className="h-[60px] border-b border-gray-800 text-xs text-gray-400 p-1"
                        >
                            {formatHourLabel(hour, timeZone, true)}
                        </div>
                    ))}
                </div>

                {/* Days */}
                <div className="flex-1 flex relative">
                    {/* Current hour highlight spanning all columns (no dot/line) */}
                    {weekDays.some(day => ymdLocal(day) === nowYmd) && (
                        <div
                            className="absolute left-0 right-0 bg-blue-900/30 z-5 pointer-events-none"
                            style={{
                                top: `${48 + getHourInTimeZone(now, timeZone) * 60}px`, // +48px for header height (h-12)
                                height: '60px'
                            }}
                        />
                    )}

                    {weekDays.map((day, dayIndex) => {
                        const isDayToday = isToday(day);
                        const isDaySelected = selectedDay && ymdLocal(day) === ymdLocal(selectedDay);
                        return (
                            <div key={dayIndex} className={`flex-1 border-r border-gray-800 last:border-r-0 relative ${isDaySelected ? 'bg-gray-800/40' : ''}`}>
                                {/* Day Header */}
                                <div className={`h-12 border-b border-gray-800 flex flex-col items-center justify-center ${isDayToday ? 'bg-blue-900/30' : isDaySelected ? 'bg-gray-700/50' : ''
                                    }`}>
                                    <div className="text-xs text-gray-400">
                                        {day.toLocaleDateString('en-US', { weekday: 'short' })}
                                    </div>
                                    <div className={`text-sm font-medium ${isDayToday ? 'text-blue-400' : isDaySelected ? 'text-gray-100' : 'text-gray-200'
                                        }`}>
                                        {day.getDate()}
                                    </div>
                                </div>

                                {/* Time Slots */}
                                <div className="relative">
                                    {hours.map(hour => (
                                        <div
                                            key={hour}
                                            className="h-[60px] border-b border-gray-800 hover:bg-gray-800/30 cursor-pointer group"
                                            onClick={() => onTimeSlotClick(day, hour)}
                                        >
                                            <div className="opacity-0 group-hover:opacity-100 p-1">
                                                <Plus className="w-3 h-3 text-gray-400" />
                                            </div>
                                        </div>
                                    ))}

                                    {/* Events */}
                                    {eventsByDay[ymdLocal(day)]?.map(event => {
                                        const { top, height, time } = getEventPosition(event, day);
                                        return (
                                            <div
                                                key={event.id}
                                                className="absolute left-1 right-1 bg-blue-600 border border-blue-500 rounded text-white text-xs p-1 cursor-pointer hover:bg-blue-500 z-10"
                                                style={{
                                                    top: `${top}px`,
                                                    height: `${Math.max(height, 30)}px`
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onEventClick(event.id);
                                                }}
                                            >
                                                <div className="font-medium truncate">{event.title}</div>
                                                <div className="text-[10px] opacity-75">{time}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}